import { ReactNode, useLayoutEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import styled from '@emotion/styled';

import {
  ExpandButton,
  OrderReturnTag,
  OrderStatusTag,
  TableBody,
  TableCell,
  TableComponent,
  TableHeader,
  TableHeaderCell,
  TableRow,
  type OrderStatus,
} from '../../components';

import { useUser } from '@innovamat/ga-features';
import {
  EmptyState,
  Grounder,
  HeadingSection,
  Loader,
} from '@innovamat/glimmer-components';
import {
  useCoursesQuery,
  useMaterialOrdersQuery,
} from '@innovamat/glow-api-client';
import { locales } from '@innovamat/localization';

import { useCartExitPrompt } from '../../hooks/use-cart-exit-prompt';
import { useCurrentMaterialsContext } from '../../providers/use-current-materials-provider';

import { EmptyClassroom } from '@innovamat/glimmer-assets';
import { SubRow } from './components/sub-row';
import { HistoryData } from './orders-returns-history.types';

const MessageBox = styled.div`
  align-items: center;
  background-color: ${({ theme }) =>
    theme.tokens.color.alias.cm.bg['bg-info'].value};
  border-radius: 1rem;
  display: inline-flex;
  justify-content: center;
  padding: 0.5rem 1rem;
`;

const LoaderText = styled.span`
  margin-left: 0.5rem;
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const TableContainer = styled.div`
  margin-top: 3rem;
`;

function OrdersReturnsHistory(): JSX.Element {
  const [tableData, setTableData] = useState<HistoryData[]>([]);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const { cart } = useCurrentMaterialsContext();

  const { user } = useUser();

  const { t } = useTranslation();

  const toggleRowExpansion = (rowId: string): void => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };

  const getOrderStatus = (rowOriginal: HistoryData): ReactNode => {
    if (
      rowOriginal.orderStatus === 'cancel' ||
      rowOriginal.orderStatus === 'completed' ||
      rowOriginal.orderStatus === 'returned'
    ) {
      return rowOriginal.orderStatus;
    }

    return rowOriginal.orderType;
  };

  const renderMaxReturnDate = (
    returnDeadlineDate: HistoryData['returnDeadlineDate'] | null
  ): string | JSX.Element => {
    if (!returnDeadlineDate) {
      return '—';
    }

    const locale = locales.getISO6391(user?.locale ?? '');

    const maxReturnDate = new Date(returnDeadlineDate);
    const today = new Date();

    if (maxReturnDate > today) {
      return t(
        'ordersReturnsHistory.returnDeadlineDate.until',
        'hasta el {{date}}',
        {
          date: convertToDateFormat(returnDeadlineDate.toString(), locale),
        }
      );
    }

    return (
      <MessageBox>
        <span>{t('ordersReturnsHistory.returnDeadlineDate.expired')}</span>
      </MessageBox>
    );
  };

  const renderTableBody = (): JSX.Element | JSX.Element[] => {
    if (isLoadingOrders) {
      return (
        <TableRow>
          <TableCell colSpan={7}>
            <LoaderContainer>
              <Loader />
              <LoaderText>{t('common.loadingContent')}</LoaderText>
            </LoaderContainer>
          </TableCell>
        </TableRow>
      );
    }

    const rows: JSX.Element[] = [];

    tableData.forEach((row) => {
      rows.push(
        <TableRow key={row.id} isExpanded={expandedRows.has(row.id)}>
          <TableCell>{row.createdAt.toString()}</TableCell>
          <TableCell>
            <OrderReturnTag
              isOrder={row.orderType === 'expand'}
              tag={t(`ordersReturnsHistory.${row.orderType}`)}
            />
          </TableCell>
          <TableCell>{row.estimateNumber}</TableCell>
          <TableCell>{row.readableId}</TableCell>
          <TableCell>
            <OrderStatusTag
              orderStatus={getOrderStatus(row) as OrderStatus}
              tag={t(`ordersReturnsHistory.orderStatus.${getOrderStatus(row)}`)}
            />
          </TableCell>
          <TableCell>{renderMaxReturnDate(row.returnDeadlineDate)}</TableCell>
          <TableCell>
            <ExpandButton
              key={row.id}
              onRowClick={() => toggleRowExpansion(row.id)}
              isExpanded={expandedRows.has(row.id)}
            />
          </TableCell>
        </TableRow>
      );

      if (expandedRows.has(row.id)) {
        rows.push(
          <SubRow
            key={`${row.id}_subrow`}
            coursesData={coursesData}
            row={{ original: row } as any}
            setTableData={setTableData}
            tableData={tableData}
          />
        );
      }
    });

    return rows;
  };

  const { data: coursesData } = useCoursesQuery(
    {
      regionCode: user?.region ?? '',
    },
    {
      enabled: !!user?.region,
      staleTime: Infinity,
    }
  );

  const organization = user?.organization;

  const { data: orders, isLoading: isLoadingOrders } = useMaterialOrdersQuery(
    {
      organizationId: organization?.id ?? '',
      academicYearId: organization?.academicYearId ?? '',
      page: 1,
      pageSize: 100,
    },
    {
      enabled: !!organization,
    }
  );

  useCartExitPrompt(cart.length === 0);

  // TODO change any
  const mapOrders = (order: any): HistoryData => {
    const locale = locales.getISO6391(user?.locale ?? '');

    return {
      ...order,
      estimateNumber: order.estimateNumber || order.document || '',
      subRows: null,
      createdAt: convertToDateFormat(order.createdAt, locale),
    } as HistoryData;
  };

  const convertToDateFormat = (date: string, locale: string): string => {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: 'long',
    }).format(new Date(date));
  };

  useLayoutEffect(() => {
    if (orders?.materialOrders) {
      setTableData(orders.materialOrders.map(mapOrders));
    }
  }, [orders]);

  return (
    <>
      <HeadingSection title={t('ordersReturnsHistory.title')} />

      {tableData.length === 0 && !isLoadingOrders ? (
        <EmptyState
          Image={EmptyClassroom}
          subtitle={t('ordersReturnsHistory.noOrdersSubtitle')}
          title={t('ordersReturnsHistory.noOrders')}
        />
      ) : (
        <TableContainer>
          <TableComponent>
            <TableHeader>
              <TableRow>
                <TableHeaderCell style={{ width: '11%' }}>
                  {t('ordersReturnsHistory.createdAt')}
                </TableHeaderCell>
                <TableHeaderCell style={{ width: '9%' }}>
                  {t('ordersReturnsHistory.orderType')}
                </TableHeaderCell>
                <TableHeaderCell style={{ width: '16%' }}>
                  {t('ordersReturnsHistory.estimateNumber')}
                </TableHeaderCell>
                <TableHeaderCell style={{ width: '11%' }}>
                  {t('ordersReturnsHistory.readableId')}
                </TableHeaderCell>
                <TableHeaderCell style={{ width: '11%' }}>
                  {t('ordersReturnsHistory.orderStatus')}
                </TableHeaderCell>
                <TableHeaderCell style={{ width: '18%' }}>
                  {t('ordersReturnsHistory.maxReturnDate')}
                </TableHeaderCell>
                <TableHeaderCell style={{ width: '6%' }}></TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>{renderTableBody()}</TableBody>
          </TableComponent>
        </TableContainer>
      )}
      <Grounder />
    </>
  );
}

export { OrdersReturnsHistory };
